import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from './firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { setupRecaptcha } from './setupRecaptcha';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export default function AuthModal({ onClose }) {
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [isSignup, setIsSignup] = useState(true);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [category, setCategory] = useState('');
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [legalConsent, setLegalConsent] = useState(false);
  const navigate = useNavigate();
  const hasRenderedRecaptcha = useRef(false);

  useEffect(() => {
    
    const renderRecaptcha = () => {
      const { render } = setupRecaptcha();
      render();
      hasRenderedRecaptcha.current = true;
    };
    if (!hasRenderedRecaptcha.current) {
      renderRecaptcha();
    } else {
      const container = document.getElementById('recaptcha-container');
      if (container && container.innerHTML.trim() === '') {
        renderRecaptcha();
      }
    }
      // ✅ Always start with checkbox unticked
   setLegalConsent(false); // Always start unchecked

  const storedConsent = localStorage.getItem("legalConsent");
  if (storedConsent === "true") {
    setLegalConsent(true); // ✅ Tick on page load
  }

  // ✅ Receive agreement from popup
  window.setLegalConsentAgreed = () => {
    setLegalConsent(true); // ✅ Live checkbox tick
  };

  return () => {
    delete window.setLegalConsentAgreed;
  };
  }, []);

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setMessage('');
  };

  const handleSignup = async () => {
    setLoading(true);
    setError('');
    if (!legalConsent) {
      setError('❌ Please agree to our legal terms before signing up.');
      setLoading(false);
      return;
    }
    try {
      await setupRecaptcha().verify();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await sendEmailVerification(user);
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        username,
        email,
        category,
        role: category === 'Other' ? customRole : role,
        subscribeNewsletter,
        agreedToLegal: true,
        createdAt: serverTimestamp(),
      });
      setMessage('✅ Account created! Please verify your email before login.');
    } catch (err) {
      handleError(err);
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (!user.emailVerified) {
        setError('❌ Please verify your email before logging in.');
        setLoading(false);
        return;
      }
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      setMessage('✅ Logged in successfully.');
      if (userData?.role?.toLowerCase() === 'superadmin') {
        navigate('/admin/investor-panel');
      } else {
        navigate('/welcome');
      }
    } catch (err) {
      handleError(err);
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError('❌ Please enter your email for password reset.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('✅ Password reset email sent.');
    } catch (err) {
      handleError(err);
    }
  };

  const handleError = (err) => {
    let msg = err.message;
    if (msg.includes('user-not-found')) msg = '❌ No account found with this email.';
    else if (msg.includes('wrong-password')) msg = '❌ Incorrect password. Please try again.';
    else if (msg.includes('invalid-email')) msg = '❌ Invalid email format.';
    else if (msg.includes('too-many-requests')) msg = '❌ Too many failed attempts. Please wait and try again.';
    else msg = '❌ Login failed. Please check your credentials.';
    setError(msg);
  };

  const getRolesByCategory = (cat) => {
    const roleMap = {
      Trader: ['Retail Trader', 'Algo Trader', 'Scalper'],
      Investor: ['Angel Investor', 'VC', 'PAMM Manager'],
      Educator: ['Mentor', 'Trainer', 'YouTuber'],
      Developer: ['Tool Creator', 'Script Coder'],
      'Legal Advisor': ['Tax Expert', 'Compliance Officer'],
    };
    return roleMap[cat] || [];
  };


  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-10 z-50">
      <div className="bg-gradient-to-b from-black via-black-300 to-black border-4 text-white rounded-lg p-6 w-full max-w-2xl shadow-lg relative flex">

        {/* ❌ Close Button */}
        <button
          onClick={() => {
            setupRecaptcha().destroy();
            onClose();
          }}
          aria-label="Close"
          className="absolute top-2 right-2 text-red-500 text-3xl font-bold leading-none hover:text-red-500 transition"
        >
          &times;
        </button>

        {/* Left Info */}
        <div className="w-1/2 pr-4 hidden md:block border-r border-gray-700">
          <h2 className="text-lg font-bold mb-2 mt-16 text-center">Welcome to Million2Trillion (M2T)</h2>
          <p className="text-xs text-center mb-4 text-gray-300">World’s #1 Verified Multi-Role Financial Marketplace</p>
          <ul className="text-sm space-y-1 text-gray-400 list-disc list-inside mt-8">
            <li>Trusted Onboarding</li>
            <li>Get News, Updates & Offers</li>
            <li>Personalized Dashboard by Role</li>
            <li>192 Financial Market Roles</li>
            <li>Access Global Market Leaders</li>
            <li>Unlimited Resources</li>
          </ul>
        </div>

        {/* Right Form */}
        <div className="w-full md:w-1/2 pl-4">
          <h2 className="text-2xl font-bold mb-4 text-center">{isSignup ? 'Create Account' : 'Log In'}</h2>
          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
          {message && <p className="text-green-400 text-sm mb-2">{message}</p>}

          <input type="text" name="fakeuser" autoComplete="username" className="hidden" />
          {isSignup && (
            <input
              type="text"
              placeholder="User name"
              className="w-full mb-2 p-2 rounded bg-white border-2 border-gray-600 text-black"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          )}

          <input
            type="email"
            placeholder="Email Address"
            className="w-full mb-2 p-2 rounded bg-white border-2 border-gray-600 text-black"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className="relative mb-2">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              className="w-full p-2 rounded bg-white border-2 border-gray-600 text-black pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-2 text-black text-lg">
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-3">Password should include uppercase, lowercase, and a number.</p>

          {isSignup && (
            <>
              <label className="text-sm font-medium">Category</label>
              <select className="w-full p-2 mb-2 bg-gray-800 rounded text-white" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">-- Select Category --</option>
                <option value="Trader">Trader</option>
                <option value="Investor">Investor</option>
                <option value="Educator">Educator</option>
                <option value="Developer">Developer</option>
                <option value="Legal Advisor">Legal Advisor</option>
                <option value="Other">Other</option>
              </select>

              {category === 'Other' ? (
                <input
                  type="text"
                  placeholder="Describe your role"
                  className="w-full p-2 mb-2 bg-gray-800 rounded text-white"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                />
              ) : (
                <select
                  className="w-full p-2 mb-2 bg-gray-800 rounded text-white"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">-- Select Role --</option>
                  {getRolesByCategory(category).map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              )}

              <div className="mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-500"
                    checked={subscribeNewsletter}
                    onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                  />
                  <span className="ml-2 text-sm text-gray-300">Subscribe to our newsletter</span>
                </label>
              </div>

              {/* ✅ Legal Consent Checkbox */}
              <div className="mb-2 text-xs text-gray-300">
                <label className="inline-flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-green-500 mt-1"
                    checked={legalConsent}
                    onChange={(e) => setLegalConsent(e.target.checked)}
                  />
                  <span>
                    I agree to the
                    <a
                      href="/legal/signup-legal-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 underline ml-1"
                    >
                      full legal terms and policies
                    </a>.
                  </span>
                </label>
              </div>
            </>
          )}

          {!isSignup && (
            <div className="text-sm mb-2 flex justify-between">
              <label><input type="checkbox" className="mr-1" /> Remember Me</label>
              <button onClick={handleResetPassword} className="text-blue-400 hover:underline">Forgot password?</button>
            </div>
          )}

          <div className="w-full mt-4 flex justify-left mb-2">
            <div id="recaptcha-container" style={{ transform: 'scale(0.97)', transformOrigin: '0 0', width: '304px' }} />
          </div>

          <button
            onClick={isSignup ? handleSignup : handleLogin}
            className="w-full py-2 mt-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
          >
            {loading ? 'Processing...' : isSignup ? 'Sign Up' : 'Log In'}
          </button>

          <p className="text-sm text-center mt-3">
            {isSignup ? 'Already have an account?' : 'New here?'}{' '}
            <button onClick={toggleMode} className="text-blue-400 hover:underline">{isSignup ? 'Log In' : 'Sign Up'}</button>
          </p>
        </div>
      </div>
    </div>
  );
}
